import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import {
  PUBLISH_TINYCMS_CONTENT,
  PUBLISH_TINYCMS_PAGES,
  UNPUBLISH_TINYCMS_CONTENT,
  UPDATE_PROJECT,
} from '../../graphql';
import { showInkToast } from './InkToast';
import { homeNotice } from './homeGap';
import { groupMissed, missedGroupWords, missedNote, retryLabel } from './missedPost';
import { groupApprovals, groupSummary, historyLine } from './wentOut';
import { InkSkeleton } from './InkSkeleton';
import { SiteHomeNotice, useSiteHomeGap } from './SiteHomeNotice';
import { useCopy } from '../../lib/copyToClipboard';
import { useAddons } from './addons';
import {
  Span,
  TodayApproval,
  TodayCampaign,
  TodayChecks,
  TodayGroup,
  TodayKind,
  TodayScheduled,
  TodayWaiting,
  useToday,
} from './todayData';
import { groupWords } from './todayGroups';
import {
  APPROVE_CAMPAIGN,
  APPROVE_SOCIAL_POST,
  channelMark,
  channelName,
  postText,
  postTime,
  ReachPost,
  REACH_CHANNELS,
  RESTORE_MISSED_SOCIAL_POST,
  RETRY_SOCIAL_POST,
  SKIP_SOCIAL_POST,
  toDate,
  campaignRange,
  shortDate,
} from '../reach/reach';

/**
 * tinycms → Today ("tinycms UX optimization review v9", Prototype v6). The
 * default page: how much changed, what waits on the person — approvable from
 * its card — what went out (undoable while it is still live), and what is
 * scheduled. Built from tinycmsToday (approvals, the agent's edits from the
 * MCP log, checks) and Reach's calendar and campaigns.
 */

const DAY = 86400000;
const QUEUE_SHOWN = 8;

const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const weekday = (d: Date) => d.toLocaleDateString([], { weekday: 'short' });

function dayLabel(date = new Date()) {
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** "22m", "3h", "2d" — how long something has waited. */
function ago(iso?: string | null) {
  const d = toDate(iso);
  if (!d) return '';
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (min < 60) return `${min}m`;
  const h = Math.round(min / 60);
  // Past a day, plain words ("3 days", "9 months"): "280d" read as a code.
  return h < 24 ? `${h}h` : formatDistanceToNowStrict(d);
}

/** "Today 6:00 PM", "Tomorrow 9:00 AM", "Sun 9:00 AM", "Oct 30, 9:00 AM". */
function when(d: Date, now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const days = Math.floor((d.getTime() - start.getTime()) / DAY);
  if (days === 0) return `Today ${time(d)}`;
  if (days === 1) return `Tomorrow ${time(d)}`;
  if (days > 1 && days < 7) return `${weekday(d)} ${time(d)}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time(d)}`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const reviewHref = (kind: TodayKind | 'dataset', id: string) =>
  kind === 'article'
    ? `/content/${id}`
    : kind === 'page'
      ? `/content?view=pages&page=${id}`
      : kind === 'form'
        ? `/content?view=forms&form=${id}`
        : '/content?view=design';

const VERB: Record<string, string> = {
  publish: 'Published',
  schedule: 'Scheduled',
  unpublish: 'Took down',
};
const WHAT: Record<string, string> = {
  article: 'an article',
  page: 'a page',
  form: 'a form',
  site: 'the site’s look and navigation',
  dataset: 'a dataset',
};

/** "Took down “Keratoconus management”", else by kind ("Published a page · version 2"). */
function describeApproval(r: TodayApproval) {
  const verb = VERB[r.action] || r.action;
  if (r.subjectTitle) return `${verb} “${r.subjectTitle}”`;
  const what = WHAT[r.kind] || r.kind;
  return `${verb} ${what}${r.versionNumber ? ` · version ${r.versionNumber}` : ''}`;
}

// ── Ribbon ──────────────────────────────────────────────────────────────────

type Tone = 'published' | 'edited' | 'waiting' | 'scheduled' | 'down';
const TONE_COLOR: Record<Tone, string> = {
  published: 'var(--ink-green)',
  edited: 'var(--ink-accent)',
  waiting: 'var(--ink-amber)',
  scheduled: 'var(--ink-accent)',
  down: 'var(--ink-ink3)',
};
const EDIT_NOUN: Record<string, string> = {
  article: 'article edit',
  page: 'page edit',
  site: 'site edit',
  form: 'form edit',
  media: 'media change',
  social: 'post edit',
};

interface Mark {
  at: number;
  tone: Tone;
  count: number;
  noun: string;
}

interface Cluster {
  at: number;
  tone: Tone;
  count: number;
  label: string;
}

/** Same-tone marks close together become one bar ("24 published, 8:43 AM"). */
function cluster(marks: Mark[], gapMs: number): Cluster[] {
  const out: Array<Cluster & { nouns: Map<string, number> }> = [];
  const open = new Map<Tone, Cluster & { nouns: Map<string, number> }>();
  for (const m of [...marks].sort((a, b) => a.at - b.at)) {
    const c = open.get(m.tone);
    if (c && m.at - c.at <= gapMs) {
      c.count += m.count;
      c.at = m.at;
      c.nouns.set(m.noun, (c.nouns.get(m.noun) || 0) + m.count);
    } else {
      const next = { at: m.at, tone: m.tone, count: m.count, label: '', nouns: new Map() };
      next.nouns.set(m.noun, m.count);
      open.set(m.tone, next);
      out.push(next);
    }
  }
  return out.map(({ nouns, ...c }) => {
    const label =
      c.tone === 'edited'
        ? nouns.size === 1
          ? plural(c.count, [...nouns.keys()][0])
          : plural(c.count, 'edit')
        : `${c.count} ${[...nouns.keys()][0]}`;
    return { ...c, label };
  });
}

const Ribbon: React.FC<{
  span: Span;
  from: Date;
  to: Date;
  clusters: Cluster[];
  waitingLabel: string | null;
  next: { at: Date; count: number } | null;
}> = ({ span, from, to, clusters, waitingLabel, next }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1000);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 1000));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const now = new Date();
  const total = to.getTime() - from.getTime();
  const pct = (t: number) => Math.min(100, Math.max(0, ((t - from.getTime()) / total) * 100));
  const nowPct = pct(now.getTime());
  const maxCount = Math.max(1, ...clusters.map((c) => c.count));
  const height = (n: number) => Math.round(18 + 92 * Math.min(1, n / maxCount));

  const ticks =
    span === 'day'
      ? [0, 6, 12, 18].map((h) => ({
          left: (h / 24) * 100,
          label: h === 0 ? '12 AM' : h === 12 ? '12 PM' : `${h % 12} ${h < 12 ? 'AM' : 'PM'}`,
        }))
      : Array.from({ length: 7 }, (_, i) => {
          const d = new Date(from.getTime() + i * DAY);
          return { left: (i / 7) * 100, label: `${weekday(d)} ${d.getDate()}` };
        });

  // Label the biggest bars that have room; "N waiting on you" always gets one.
  // A label hangs right of its bar (left of it past the middle) and flips
  // sides rather than run into another; one that fits neither way is left off.
  const labelText = (c: Cluster) => (c.tone === 'waiting' ? waitingLabel : c.label) || '';
  const sideOf = new Map<Cluster, 'left' | 'right'>();
  const taken: Array<[number, number]> = [];
  const fits = (a: number, b: number) =>
    a >= 0 && b <= width && taken.every(([x, y]) => b + 10 <= x || a >= y + 10);
  const tryLabel = (c: Cluster) => {
    const x = (pct(c.at) / 100) * width;
    const w = Math.max(labelText(c).length * 7, 64) + 10;
    const order: Array<'left' | 'right'> = x > width * 0.55 ? ['left', 'right'] : ['right', 'left'];
    for (const side of order) {
      const [a, b] = side === 'right' ? [x, x + w] : [x - w, x];
      if (fits(a, b)) {
        taken.push([a, b]);
        sideOf.set(c, side);
        return;
      }
    }
  };
  const amber = clusters.filter((c) => c.tone === 'waiting');
  const lastAmber = amber[amber.length - 1];
  // The "now" pill and the next-scheduled note sit above the bars; keep clear.
  if (next) taken.push([width - 130, width]);
  if (lastAmber && waitingLabel) tryLabel(lastAmber);
  for (const c of [...clusters].sort((a, b) => b.count - a.count)) {
    if (sideOf.size >= 6 || c.tone === 'waiting') continue;
    tryLabel(c);
  }

  // Bars at the same spot sit side by side rather than on top of each other.
  const placed: Array<{ c: Cluster; left: number; shift: number }> = [];
  for (const c of clusters) {
    const left = pct(c.at);
    const shift = placed.filter((p) => Math.abs(p.left - left) < 0.4).length * 8;
    placed.push({ c, left, shift });
  }

  return (
    <>
      <div className="today-ribbon" ref={ref}>
        <div className="today-future" style={{ left: `${nowPct}%` }} />
        {ticks.map((t) => (
          <React.Fragment key={t.label}>
            <div className="today-tick" style={{ left: `${t.left}%` }} />
            <div className="today-tick-label ink-mono" style={{ left: `${t.left}%` }}>
              {t.label}
            </div>
          </React.Fragment>
        ))}
        {placed.map(({ c, left, shift }, i) => {
          const h = height(c.count);
          const side = sideOf.get(c);
          const label = labelText(c);
          const at = new Date(c.at);
          return (
            <React.Fragment key={`${c.tone}-${c.at}-${i}`}>
              <div
                className="today-bar"
                title={`${label || c.label} · ${span === 'week' ? `${weekday(at)} ` : ''}${time(at)}`}
                style={{
                  left: `calc(${left}% + ${shift}px)`,
                  height: h,
                  background: TONE_COLOR[c.tone],
                }}
              />
              {side && label ? (
                <div
                  className="today-bar-label"
                  data-side={side}
                  style={{
                    left: `calc(${left}% + ${shift}px)`,
                    bottom: h + 8,
                    color:
                      c.tone === 'waiting'
                        ? 'var(--ink-amber)'
                        : c.tone === 'published'
                          ? 'var(--ink-ink)'
                          : 'var(--ink-ink2)',
                  }}
                >
                  {label}
                  <div className="ink-mono">
                    {span === 'week' ? `${weekday(at)} ` : ''}
                    {time(at)}
                  </div>
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
        <div className="today-now" style={{ left: `${nowPct}%` }} />
        <div className="today-now-pill ink-mono" style={{ left: `${nowPct}%` }}>
          {span === 'week' ? `${weekday(now)} ` : ''}
          {time(now)}
        </div>
        {next ? (
          <Link to="/content" className="today-next">
            {when(next.at)}
            <div>{plural(next.count, 'scheduled', 'scheduled')} →</div>
          </Link>
        ) : null}
      </div>
    </>
  );
};

// ── Queue ───────────────────────────────────────────────────────────────────

/** One post on a Reach card: network, account and time, the caption's first line, its photo. */
interface PostPreview {
  id: string;
  mark: string;
  network: string;
  meta: string;
  text: string;
  image: string | null;
}

interface QueueCard {
  key: string;
  chip: string;
  corner: string;
  /** A content card's hero, when it has one. Cards with nothing to show stay compact. */
  image?: string | null;
  /** The posts a Reach card approves, retries or skips, shown one per row. */
  posts?: PostPreview[];
  title: string;
  note?: string | null;
  check?: { text: string; tone: 'ok' | 'warn' | 'block' } | null;
  reviewTo: string;
  /** Where Fix goes: the review screen with its open checks marked, when it can. */
  fixTo?: string;
  /** The review link's words: "Review both" or "Review all" for items approved together. */
  reviewLabel?: string;
  cta: string;
  blocked?: boolean;
  approve: () => Promise<void>;
  /**
   * A campaign whose end date passed unapproved (Prototype v7, "Spring set
   * preview"): no Approve. "Reschedule" gives a prompt asking the agent for
   * new dates; "Dismiss" (approve here) takes it off Today, with Undo.
   */
  ended?: { prompt: string };
  /**
   * Social posts that didn't go out (one, or several on a channel for one
   * reason): approve posts them now, and Skip ("Skip all") drops them.
   */
  missed?: { skip: () => Promise<void>; skipLabel?: string };
}

const CHECK_COLOR = {
  ok: 'var(--ink-green-ink)',
  warn: 'var(--ink-amber)',
  block: 'var(--ink-red)',
};

/** The kind chip and the time, the card's first line (Prototype v13). */
const CardHead: React.FC<{ card: QueueCard; tone?: 'failed' }> = ({ card, tone }) => (
  <div className="today-card-head">
    <span className="today-chip" data-tone={tone}>
      {card.chip}
    </span>
    {card.corner ? <span className="today-corner ink-mono">{card.corner}</span> : null}
  </div>
);

/** Rows shown before "+ N more": enough to see what is going out, short enough for the grid. */
const POSTS_SHOWN = 3;

const PostRows: React.FC<{ posts?: PostPreview[] }> = ({ posts }) => {
  if (!posts?.length) return null;
  const more = posts.length - POSTS_SHOWN;
  return (
    <div className="today-posts">
      {posts.slice(0, POSTS_SHOWN).map((p) => (
        <div key={p.id} className="today-post">
          {p.image ? <img className="today-post-img" src={p.image} alt="" /> : null}
          <div className="today-post-main">
            <div className="today-post-meta">
              <span className="today-post-mark" title={p.network}>
                {p.mark}
              </span>
              <span className="today-post-where">{p.meta}</span>
            </div>
            <div className="today-post-text">{p.text || 'No caption'}</div>
          </div>
        </div>
      ))}
      {more > 0 ? <div className="today-posts-more">+ {plural(more, 'more post')}</div> : null}
    </div>
  );
};

const Card: React.FC<{
  card: QueueCard;
  busy: boolean;
  onApprove: () => void;
  onSkip?: () => void;
}> = (props) =>
  props.card.missed ? (
    <MissedCard {...props} />
  ) : props.card.ended ? (
    <EndedCard {...props} />
  ) : (
    <OpenCard {...props} />
  );

/** Posts that didn't go out: why, then Skip / Post now (Skip all, for several). */
const MissedCard: React.FC<{
  card: QueueCard;
  busy: boolean;
  onApprove: () => void;
  onSkip?: () => void;
}> = ({ card, busy, onApprove, onSkip }) => (
  <div className="ink-card today-card" data-kind="missed">
    <div className="today-card-body">
      <CardHead card={card} tone="failed" />
      <div className="today-card-title">{card.title}</div>
      {card.note ? <div className="today-card-note">{card.note}</div> : null}
      <PostRows posts={card.posts} />
      <div className="today-card-actions">
        <button type="button" className="ink-btn" onClick={onSkip} disabled={busy}>
          {card.missed?.skipLabel || 'Skip'}
        </button>
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          onClick={onApprove}
          disabled={busy}
        >
          {card.cta}
        </button>
      </div>
    </div>
  </div>
);

/** An ended campaign: Dismiss / Reschedule, and the agent prompt when asked to reschedule. */
const EndedCard: React.FC<{ card: QueueCard; busy: boolean; onApprove: () => void }> = ({
  card,
  busy,
  onApprove,
}) => {
  const [asking, setAsking] = useState(false);
  const { copy, state } = useCopy();
  const copied = state('prompt');
  return (
    <div className="ink-card today-card">
      <div className="today-card-body">
        <CardHead card={card} />
        <div className="today-card-title">{card.title}</div>
        {card.note ? <div className="today-card-note">{card.note}</div> : null}
        {asking ? (
          <div className="today-reschedule">
            <span className="ink-muted">Ask your agent for new dates:</span>
            <div className="pg-agent-prompt">{card.ended!.prompt}</div>
          </div>
        ) : null}
        <div className="today-card-actions">
          <button type="button" className="ink-btn" onClick={onApprove} disabled={busy}>
            Dismiss
          </button>
          {asking ? (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={(e) => copy('prompt', card.ended!.prompt, e)}
            >
              {copied === 'copied'
                ? 'Copied'
                : copied === 'failed'
                  ? 'Couldn’t copy'
                  : 'Copy prompt'}
            </button>
          ) : (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => setAsking(true)}
              disabled={busy}
            >
              Reschedule
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const OpenCard: React.FC<{ card: QueueCard; busy: boolean; onApprove: () => void }> = ({
  card,
  busy,
  onApprove,
}) => (
  <div className="ink-card today-card">
    {card.image ? (
      <div className="today-card-image">
        <img src={card.image} alt="" />
      </div>
    ) : null}
    <div className="today-card-body">
      <CardHead card={card} />
      <div className="today-card-title">{card.title}</div>
      {card.note ? <div className="today-card-note">{card.note}</div> : null}
      <PostRows posts={card.posts} />
      {card.check ? (
        <div className="today-card-check" style={{ color: CHECK_COLOR[card.check.tone] }}>
          {card.check.text}
        </div>
      ) : null}
      {card.check && card.check.tone !== 'ok' ? (
        // An open warning (Prototype v7): fixing it is the primary action and
        // approving becomes "… anyway" until it's resolved. A blocking issue
        // can't be approved from the card at all.
        <div className="today-card-actions">
          <button
            type="button"
            className="ink-btn"
            onClick={onApprove}
            disabled={busy || card.blocked}
            title={card.blocked ? 'Fix the blocking issue first' : undefined}
          >
            {card.cta} anyway
          </button>
          <Link to={card.fixTo || card.reviewTo} className="ink-btn ink-btn-primary">
            {fixLabel(card.check.text)}
          </Link>
        </div>
      ) : (
        <div className="today-card-actions">
          <Link to={card.reviewTo} className="ink-btn">
            {card.reviewLabel || 'Review'}
          </Link>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            onClick={onApprove}
            disabled={busy || card.blocked}
          >
            {card.cta}
          </button>
        </div>
      )}
    </div>
  </div>
);

/** The fix button's words for a card's first open check (Prototype v7: "Fix claim", "Add alt text"). */
function fixLabel(check: string): string {
  if (/alt text/i.test(check)) return 'Add alt text';
  if (/no image|no share image|image missing/i.test(check)) return 'Add image';
  if (/claim/i.test(check)) return 'Fix claim';
  if (/link/i.test(check)) return 'Fix link';
  return 'Fix';
}

function checkLine(c: TodayChecks | null): QueueCard['check'] {
  if (!c) return null;
  const more = c.more > 0 ? ` (+${c.more} more)` : '';
  if (c.blocking) return { tone: 'block', text: `● ${c.first}${more}` };
  if (c.warnings) return { tone: 'warn', text: `● ${c.first}${more}` };
  return { tone: 'ok', text: '✓ Checks passed' };
}

const CHIP: Record<TodayKind, [string, string]> = {
  article: ['Publish', 'Update'],
  page: ['New page', 'Page change'],
  form: ['New form', 'Form change'],
  site: ['Site change', 'Site change'],
};

// ── Page ────────────────────────────────────────────────────────────────────

export const CmsToday: React.FC = () => {
  const { accountId } = useAccount();
  const [span, setSpan] = useState<Span>('day');
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const {
    window: win,
    loading,
    reachLoading,
    error,
    data,
    waitingPosts,
    missedPosts,
    posts,
    campaigns,
    need,
    refetch,
  } = useToday(span);
  const homeGapState = useSiteHomeGap(accountId);
  const homeWarning = homeGapState.gap ? homeNotice(homeGapState.gap) : null;

  const [publish] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [publishPages] = useMutation(PUBLISH_TINYCMS_PAGES);
  const [unpublish] = useMutation(UNPUBLISH_TINYCMS_CONTENT);
  const [approvePost] = useMutation(APPROVE_SOCIAL_POST);
  const [retryPost] = useMutation(RETRY_SOCIAL_POST);
  const [skipPost] = useMutation(SKIP_SOCIAL_POST);
  const [restoreMissed] = useMutation(RESTORE_MISSED_SOCIAL_POST);
  const [approveCampaign] = useMutation(APPROVE_CAMPAIGN);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  // Account names for the post rows on Reach cards, read once rather than per post.
  const { reachOn } = useAddons();
  const channelsQ = useQuery<{
    socialProfiles: {
      id: string;
      platform: string;
      profileName?: string | null;
      profileUsername?: string | null;
    }[];
  }>(REACH_CHANNELS, { variables: { accountId }, skip: !accountId || !reachOn });
  const accountNames = useMemo(
    () =>
      new Map(
        (channelsQ.data?.socialProfiles || []).map((c) => [
          c.id,
          c.profileName || (c.profileUsername ? `@${c.profileUsername.replace(/^@/, '')}` : ''),
        ])
      ),
    [channelsQ.data]
  );
  const contentLists = ['GetTinycmsArticles', 'GetTinycmsPages', 'GetTinycmsForms'];

  const from = useMemo(() => new Date(win.from), [win.from]);
  const to = useMemo(() => new Date(win.to), [win.to]);
  const approvals = useMemo(() => data?.approvals || [], [data]);
  const agent = useMemo(() => data?.agent || [], [data]);
  const sent = useMemo(
    () =>
      posts.filter((p) => {
        const t = toDate(p.socialPublishDate);
        return (p.reachStatus === 'posted' || p.reachStatus === 'publishing') && t && t >= from;
      }),
    [posts, from]
  );

  // ── Cards ─────────────────────────────────────────────────────────────────
  const cards: QueueCard[] = useMemo(() => {
    const previewOf = (p: ReachPost): PostPreview => {
      const t = postTime(p);
      const network = channelName(p.platform);
      const account = accountNames.get(p.socialProfileId) || network;
      return {
        id: p.id,
        mark: channelMark(p.platform),
        network,
        meta: t ? `${account} · ${when(t)}` : account,
        text: postText(p).split('\n')[0].trim(),
        image: p.imageUrl || null,
      };
    };
    const waitingList = data?.waiting || [];
    const byId = new Map(waitingList.map((w) => [w.id, w]));
    const groupsById = new Map((data?.groups || []).map((g) => [g.id, g]));
    const shown = new Set<string>();

    // A page and the waiting forms it places: one card, approved together.
    const groupCard = (g: TodayGroup): QueueCard => {
      const members = (ids: string[]) =>
        ids.map((id) => byId.get(id)).filter((w): w is TodayWaiting => Boolean(w));
      const pages = members(g.pages);
      const forms = members(g.forms);
      const asMember = (w: TodayWaiting) => ({ ...w, kind: w.kind as 'page' | 'form' });
      const words = groupWords(pages.map(asMember), forms.map(asMember));
      const first = pages[0];
      const latest = [...pages, ...forms]
        .map((w) => w.updatedAt || '')
        .sort()
        .pop();
      return {
        key: `group:${g.id}`,
        chip: words.chip,
        corner: ago(latest),
        image: first.heroUrl,
        title: words.title,
        note: words.note,
        check: checkLine(g.checks),
        reviewTo: reviewHref('page', first.id),
        reviewLabel: words.reviewLabel,
        fixTo: `${reviewHref('page', first.id)}&fix=1`,
        cta: words.cta,
        blocked: Boolean(g.checks.blocking),
        approve: async () => {
          if (
            !window.confirm(
              `${words.confirm}\n\nYour approval is recorded with your name and the time.`
            )
          )
            return;
          const { data: res } = await publishPages({
            variables: {
              accountId,
              pages: pages.map((p) => ({ pageId: p.id, contentHash: p.workingHash })),
              forms: forms.map((f) => ({ formId: f.id, contentHash: f.workingHash })),
            },
            refetchQueries: contentLists,
          });
          const r = res?.publishTinycmsPages;
          const bad = (r?.items || []).find((i: any) => i.ok === false);
          if (r?.ok) showInkToast(words.done);
          else
            showInkToast(
              `Nothing was published: ${bad?.message || r?.message || 'refused'}. Open ${words.reviewLabel}.`
            );
        },
      };
    };

    const content = waitingList.flatMap((w): QueueCard[] => {
      const g = w.group ? groupsById.get(w.group) : undefined;
      if (g) {
        if (shown.has(g.id)) return [];
        shown.add(g.id);
        return [groupCard(g)];
      }
      const title = w.title || (w.kind === 'site' ? 'Site look and navigation' : 'Untitled');
      return [
        {
          key: `${w.kind}:${w.id}`,
          chip: CHIP[w.kind][w.live ? 1 : 0],
          corner: ago(w.updatedAt),
          image: w.heroUrl,
          title,
          note: w.note,
          check: checkLine(w.checks),
          reviewTo: reviewHref(w.kind, w.id),
          fixTo:
            w.kind === 'page' || w.kind === 'form'
              ? `${reviewHref(w.kind, w.id)}&fix=1`
              : undefined,
          cta: 'Approve',
          blocked: Boolean(w.checks?.blocking),
          approve: async () => {
            if (
              !window.confirm(
                `Approve and publish “${title}” now?\n\nYour approval is recorded with your name and the time.`
              )
            )
              return;
            const { data: res } = await publish({
              variables: {
                accountId,
                kind: w.kind,
                id: w.kind === 'site' ? undefined : w.id,
                action: 'publish',
                contentHash: w.workingHash,
              },
              refetchQueries: contentLists,
            });
            const r = res?.publishTinycmsContent;
            if (r?.ok) showInkToast(`Published “${title}”.`);
            else
              showInkToast(`Not published: ${r?.message || r?.refused || 'refused'}. Open Review.`);
          },
        },
      ];
    });

    // Undo of Skip / Post now: each post is missed again at its old time.
    const undoFor = (list: ReachPost[]) => ({
      label: 'Undo',
      onClick: () =>
        void Promise.all(
          list.map((p) => {
            const at = toDate(p.scheduleTime);
            return at
              ? restoreMissed({
                  variables: {
                    id: p.id,
                    scheduleTime: at.toISOString(),
                    reviewStatus: p.reviewStatus === 'approved' ? 'approved' : null,
                  },
                })
              : null;
          })
        )
          .then(() => refetch())
          .catch((err) => showInkToast(err instanceof Error ? err.message : 'Couldn’t undo that.')),
    });
    const forEach = async (list: ReachPost[], fn: (p: ReachPost) => Promise<unknown>) => {
      const res = await Promise.allSettled(list.map(fn));
      const bad = res.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (bad.length)
        throw new Error(
          `${list.length - bad.length} of ${list.length} went through. ${
            bad[0].reason instanceof Error ? bad[0].reason.message : 'The rest didn’t.'
          }`
        );
    };

    // Missed posts on one channel, for one reason, are one card (Prototype v7).
    const missed = groupMissed(missedPosts).map((list): QueueCard => {
      const p = list[0];
      const channel = channelName(p.platform);
      const undo = undoFor(list);
      if (list.length === 1) {
        const t = postTime(p);
        return {
          key: `missed:${p.id}`,
          chip: 'Didn’t post',
          corner: t ? when(t) : '',
          title: `${channel} post didn’t go out`,
          // The post's own row quotes it, so the note doesn't.
          note: missedNote(channel, t, p.failure, ''),
          posts: [previewOf(p)],
          check: null,
          reviewTo: '/reach/calendar',
          cta: retryLabel(t),
          approve: async () => {
            await retryPost({ variables: { id: p.id } });
            showInkToast('Posting it now. It goes out in a minute.', undo);
          },
          missed: {
            skip: async () => {
              await skipPost({ variables: { id: p.id } });
              showInkToast('Skipped. It won’t go out.', undo);
            },
          },
        };
      }
      const w = missedGroupWords(
        channel,
        list.map(postTime).filter((t): t is Date => Boolean(t)),
        p.failure
      );
      return {
        key: `missed:${list.map((x) => x.id).join(',')}`,
        chip: 'Didn’t post',
        corner: w.corner,
        title: w.title,
        note: w.note,
        posts: list.map(previewOf),
        check: null,
        reviewTo: '/reach/calendar',
        cta: w.cta,
        approve: async () => {
          await forEach(list, (x) => retryPost({ variables: { id: x.id } }));
          showInkToast(w.posted, undo);
        },
        missed: {
          skipLabel: w.skip,
          skip: async () => {
            await forEach(list, (x) => skipPost({ variables: { id: x.id } }));
            showInkToast(w.skipped, undo);
          },
        },
      };
    });

    const camps = campaigns.map((c: TodayCampaign): QueueCard => {
      if (c.kind === 'always_on') {
        return {
          key: `campaign:${c.id}`,
          chip: 'Reach · always on',
          corner: ago(c.createdAt),
          title: c.name,
          note: 'Your agent set this up. Nothing posts until you turn it on.',
          check: null,
          reviewTo: `/reach/campaigns?campaign=${c.id}`,
          cta: 'Turn it on',
          approve: async () => {
            if (!window.confirm(`Turn on “${c.name}”? It starts posting matching articles.`))
              return;
            await approveCampaign({ variables: { id: c.id } });
            showInkToast(`${c.name} is on.`);
          },
        };
      }
      const waiting = c.posts.filter((p) => p.status === 'in_review');
      const end = toDate(c.deadline);
      const dayStart = new Date();
      dayStart.setHours(0, 0, 0, 0);
      if (end && end.getTime() < dayStart.getTime()) {
        // Its end date passed and nobody approved it: approving now means nothing.
        const ended = shortDate(end);
        const previous = c.status;
        return {
          key: `campaign:${c.id}`,
          chip: 'Reach campaign',
          corner: `Waiting ${formatDistanceToNowStrict(toDate(c.createdAt) || end)}`,
          title: c.name,
          note: `Ended ${ended} · never approved. Nothing went out.`,
          check: null,
          reviewTo: `/reach/campaigns?campaign=${c.id}`,
          cta: 'Dismiss',
          ended: {
            prompt: `The “${c.name}” campaign ended ${ended} without being approved, so nothing went out. Suggest new dates, update its posts to match, and send it back to me for approval in TinyCMS.`,
          },
          // Dismiss: mark it completed — a dated campaign can't be paused — so it
          // leaves Today. Nothing is deleted; it stays in Reach → Campaigns.
          approve: async () => {
            await updateProject({ variables: { id: c.id, input: { status: 'completed' } } });
            showInkToast(`${c.name} dismissed. It’s still in Reach → Campaigns.`, {
              label: 'Undo',
              onClick: () =>
                void updateProject({ variables: { id: c.id, input: { status: previous } } }).then(
                  () => refetch()
                ),
            });
          },
        };
      }
      // The campaign's own dates, as Reach → Campaigns shows them (a waiting
      // post's date is not when the campaign starts).
      const range = campaignRange(c);
      const runs = range ? `Runs ${range}.` : '';
      return {
        key: `campaign:${c.id}`,
        chip: 'Reach campaign',
        corner: `Waiting ${ago(c.createdAt)}`,
        title: c.name,
        note: `${plural(waiting.length, 'post')} waiting.${runs ? ` ${runs}` : ''}`,
        check: null,
        reviewTo: `/reach/campaigns?campaign=${c.id}`,
        cta: 'Approve campaign',
        approve: async () => {
          if (!window.confirm(`Approve the “${c.name}” campaign? Its posts go on the calendar.`))
            return;
          const { data: res } = await approveCampaign({ variables: { id: c.id } });
          const r = res?.approveCampaign;
          showInkToast(
            `Approved. ${r?.scheduled || 0} on the calendar.${
              r?.undated ? ` ${r.undated} need a date before going out.` : ''
            }`
          );
        },
      };
    });

    const byChannel = new Map<string, ReachPost[]>();
    for (const p of waitingPosts) {
      const k = (p.platform || '').toLowerCase();
      byChannel.set(k, [...(byChannel.get(k) || []), p]);
    }
    const postCards = [...byChannel].map(([platform, list]): QueueCard => {
      const times = list.map(postTime).filter(Boolean) as Date[];
      const first = times.sort((a, b) => a.getTime() - b.getTime())[0];
      const last = times[times.length - 1];
      const daysOut = first && last ? Math.ceil((last.getTime() - Date.now()) / DAY) : 0;
      return {
        key: `posts:${platform}`,
        chip: 'Reach posts',
        corner: !first
          ? ''
          : list.length > 1 && daysOut > 1
            ? `next ${daysOut} days`
            : when(first).toLowerCase(),
        title: plural(
          list.length,
          `post for ${channelName(platform)}`,
          `posts for ${channelName(platform)}`
        ),
        // Each post has its own row below, so the note only says when.
        note: !first
          ? null
          : list.length === 1
            ? `Goes out ${when(first)}.`
            : daysOut > 1
              ? `Scheduled over the next ${daysOut} days.`
              : `Scheduled from ${when(first)}.`,
        posts: list.map(previewOf),
        check: null,
        reviewTo: '/reach/calendar',
        cta: list.length > 1 ? `Approve all ${list.length}` : 'Approve',
        approve: async () => {
          for (const p of list) await approvePost({ variables: { id: p.id } });
          showInkToast(`Approved ${plural(list.length, 'post')} for ${channelName(platform)}.`);
        },
      };
    });

    return [...content, ...missed, ...camps, ...postCards];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, campaigns, waitingPosts, missedPosts, accountId, accountNames]);

  const run = async (card: QueueCard, fn: () => Promise<void> = card.approve) => {
    setBusy(card.key);
    try {
      await fn();
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  // ── Ribbon data ───────────────────────────────────────────────────────────
  const clusters = useMemo(() => {
    const marks: Mark[] = [];
    for (const a of approvals) {
      const at = new Date(a.recordedAt).getTime();
      if (a.action === 'publish')
        marks.push({ at, tone: 'published', count: 1, noun: 'published' });
      else if (a.action === 'schedule')
        marks.push({ at, tone: 'scheduled', count: 1, noun: 'scheduled' });
      else marks.push({ at, tone: 'down', count: 1, noun: 'taken down' });
    }
    for (const g of agent) {
      marks.push({
        at: new Date(g.at).getTime(),
        tone: 'edited',
        count: g.count,
        noun: EDIT_NOUN[g.group] || 'edit',
      });
    }
    for (const p of sent) {
      const t = toDate(p.socialPublishDate);
      if (t) marks.push({ at: t.getTime(), tone: 'published', count: 1, noun: 'published' });
    }
    for (const w of data?.waiting || []) {
      const t = toDate(w.updatedAt);
      if (t && t >= from)
        marks.push({ at: t.getTime(), tone: 'waiting', count: 1, noun: 'waiting' });
    }
    // Waiting from before the window still shows, at "now".
    const amberInWindow = marks.some((m) => m.tone === 'waiting');
    if (need && !amberInWindow) {
      marks.push({ at: Date.now(), tone: 'waiting', count: 1, noun: 'waiting' });
    }
    return cluster(marks, span === 'day' ? 30 * 60000 : 6 * 3600000);
  }, [approvals, agent, sent, data, from, need, span]);

  const changes = approvals.length + agent.reduce((n, g) => n + g.count, 0) + sent.length;

  const upcoming: TodayScheduled[] = useMemo(
    () => (data?.scheduled || []).filter((s) => new Date(s.at).getTime() >= Date.now()),
    [data]
  );
  const next = upcoming[0] ? { at: new Date(upcoming[0].at), count: upcoming.length } : null;

  // ── Went out ──────────────────────────────────────────────────────────────
  const wentOut = useMemo(() => {
    // Undo takes the item off the site; the site's own look can't be taken down.
    const undoOf = (a: TodayApproval | null) =>
      a && a.action === 'publish' && a.stillLive && a.kind !== 'site'
        ? async () => {
            const name = a.subjectTitle ? `“${a.subjectTitle}”` : 'this';
            if (!window.confirm(`Take ${name} off the site? You can publish it again later.`))
              return;
            await unpublish({
              variables: {
                accountId,
                kind: a.kind,
                id: a.subjectId,
                reason: 'Undone from Today',
              },
              refetchQueries: contentLists,
            });
            showInkToast(`Took down ${name}.`);
          }
        : undefined;
    const rows: Array<{
      key: string;
      at: Date;
      text: string;
      obj: string;
      undo?: () => Promise<void>;
      undone?: boolean;
      history?: ReturnType<typeof historyLine>[];
    }> = groupApprovals(approvals).map((g) => {
      const a = g.latest;
      if (g.events.length === 1) {
        return {
          key: a.id,
          at: new Date(a.recordedAt),
          text: describeApproval(a),
          obj: [
            a.subjectPath,
            a.approvedBy
              ? `${a.action === 'unpublish' ? 'by' : 'approved by'} ${a.approvedBy}${
                  a.via === 'ui' ? ' here' : a.via === 'mcp' ? ' in chat with the agent' : ''
                }`
              : null,
            a.action === 'schedule' && a.scheduledFor
              ? `for ${when(new Date(a.scheduledFor))}`
              : null,
          ]
            .filter(Boolean)
            .join(' · '),
          undone: a.action === 'publish' && !a.stillLive,
          undo: undoOf(a),
        };
      }
      return {
        key: g.key,
        at: new Date(a.recordedAt),
        text: describeApproval({ ...a, versionNumber: null }),
        obj: [a.subjectPath, groupSummary(g, span === 'week' ? 'this week' : 'today')]
          .filter(Boolean)
          .join(' · '),
        undone: a.action === 'publish' && !g.live,
        undo: undoOf(g.live),
        history: g.events.map(historyLine),
      };
    });
    for (const p of sent) {
      rows.push({
        key: `post:${p.id}`,
        at: toDate(p.socialPublishDate)!,
        text: `Posted to ${channelName(p.platform)}`,
        obj: postText(p),
      });
    }
    return rows.sort((a, b) => b.at.getTime() - a.at.getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvals, sent, accountId, span]);
  const [histOpen, setHistOpen] = useState<Record<string, boolean>>({});

  const undo = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  const stamp = (d: Date) => (span === 'week' ? `${weekday(d)} ${time(d)}` : time(d));
  // The queue, its count and Went out include Reach, so they wait for it too.
  const queueLoading = loading || reachLoading;
  const shown = showAll ? cards : cards.slice(0, QUEUE_SHOWN);
  const headA = loading
    ? 'Today'
    : span === 'week'
      ? `${plural(changes, 'change')} this week.`
      : changes
        ? `${plural(changes, 'change')} so far.`
        : 'No changes yet today.';

  return (
    <main className="ink-body">
      <div className="today-head">
        <div>
          <div className="ink-muted">{dayLabel()}</div>
          <h1 className="ink-display">
            {headA}{' '}
            {queueLoading ? null : (
              <span style={{ color: need ? 'var(--ink-amber)' : 'var(--ink-green-ink)' }}>
                {need ? `${need} need${need === 1 ? 's' : ''} you.` : 'You’re all caught up.'}
              </span>
            )}
          </h1>
        </div>
        <div className="today-head-actions">
          <Link to="/cms/history" className="today-history">
            Approval history →
          </Link>
          <div className="ink-seg today-seg" role="tablist" aria-label="Range">
            {(['day', 'week'] as Span[]).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={span === s}
                data-active={span === s}
                onClick={() => setSpan(s)}
              >
                {s === 'day' ? 'Day' : 'Week'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {homeWarning ? (
        <SiteHomeNotice
          text={homeWarning.text}
          action={{ label: homeWarning.label, to: homeWarning.to }}
        />
      ) : null}

      {error && !data ? (
        <div className="ink-muted" role="alert" style={{ marginTop: 16, color: 'var(--ink-red)' }}>
          Today couldn’t load just now. What’s waiting is still in Content.{' '}
          <button type="button" className="today-undo" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      ) : null}

      <Ribbon
        span={span}
        from={from}
        to={to}
        clusters={clusters}
        waitingLabel={need ? `${need} waiting on you` : null}
        next={next}
      />
      <div className="today-legend">
        <span>
          <i style={{ background: 'var(--ink-green)' }} />
          Published
        </span>
        <span>
          <i style={{ background: 'var(--ink-accent)' }} />
          Edited / scheduled
        </span>
        <span>
          <i style={{ background: 'var(--ink-amber)' }} />
          Waiting on you
        </span>
      </div>

      {cards.length ? (
        <>
          <div className="today-queue">
            {shown.map((c) => (
              <Card
                key={c.key}
                card={c}
                busy={busy === c.key}
                onApprove={() => run(c)}
                onSkip={c.missed ? () => run(c, c.missed!.skip) : undefined}
              />
            ))}
          </div>
          {cards.length > QUEUE_SHOWN ? (
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              style={{ marginTop: 16 }}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? 'Show fewer' : `Show all ${cards.length}`}
            </button>
          ) : null}
        </>
      ) : queueLoading ? (
        <InkSkeleton rows={2} label="Loading what needs you" style={{ marginTop: 28 }} />
      ) : (
        <div className="today-empty">
          <span
            className="ink-dot"
            style={{ width: 10, height: 10, background: 'var(--ink-green)' }}
          />
          <div>
            <div style={{ font: '600 16px/1.3 var(--ink-font)' }}>Nothing waiting on you</div>
            <div className="ink-muted" style={{ marginTop: 3 }}>
              Your agent asks here before anything goes live.
            </div>
          </div>
        </div>
      )}

      <div className="today-columns">
        <div>
          <div className="today-col-title">
            {span === 'week' ? 'Went out this week' : 'Went out today'}
          </div>
          <div className="ink-muted" style={{ marginTop: 4 }}>
            Each one went live once someone approved it. You can undo any that are still live.
          </div>
          <div style={{ marginTop: 14 }}>
            {queueLoading ? <InkSkeleton rows={3} label="Loading what went out" /> : null}
            {queueLoading
              ? null
              : wentOut.map((e) => (
                  <div key={e.key} className="today-row today-row-out">
                    <span className="ink-mono today-row-time">{stamp(e.at)}</span>
                    <div style={{ opacity: e.undone ? 0.55 : 1, minWidth: 0 }}>
                      <div className="today-row-text">{e.text}</div>
                      {e.obj ? <div className="today-row-obj">{e.obj}</div> : null}
                      {e.history ? (
                        <>
                          <button
                            type="button"
                            className="today-hist-toggle"
                            aria-expanded={Boolean(histOpen[e.key])}
                            onClick={() => setHistOpen((s) => ({ ...s, [e.key]: !s[e.key] }))}
                          >
                            {histOpen[e.key]
                              ? 'Hide history'
                              : `History (${plural(
                                  e.history.length,
                                  e.history.every((h) => h.status) ? 'version' : 'approval'
                                )})`}
                          </button>
                          {histOpen[e.key] ? (
                            <div className="today-hist">
                              {e.history.map((h) => (
                                <div key={h.id} className="today-hist-row">
                                  <span className="ink-mono">{stamp(h.at)}</span>
                                  <span>{h.version}</span>
                                  <span>
                                    {h.what}
                                    {h.status ? (
                                      <>
                                        {' · '}
                                        <span
                                          style={{
                                            color:
                                              h.status === 'Live'
                                                ? 'var(--ink-green-ink)'
                                                : 'var(--ink-ink3)',
                                          }}
                                        >
                                          {h.status}
                                        </span>
                                      </>
                                    ) : null}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                    {e.undo ? (
                      <button
                        type="button"
                        className="today-undo"
                        disabled={busy === e.key}
                        onClick={() => undo(e.key, e.undo!)}
                      >
                        Undo
                      </button>
                    ) : e.undone ? (
                      <span className="today-row-obj">No longer live</span>
                    ) : (
                      <span />
                    )}
                  </div>
                ))}
            {!wentOut.length && !queueLoading ? (
              <div className="ink-muted">
                Nothing went out {span === 'week' ? 'this week' : 'yet today'}.
              </div>
            ) : null}
          </div>
        </div>
        <div>
          <div className="today-col-title">Coming up</div>
          <div className="ink-muted" style={{ marginTop: 4 }}>
            Scheduled to publish automatically.
          </div>
          <div style={{ marginTop: 14 }}>
            {loading ? <InkSkeleton rows={2} label="Loading what's coming up" /> : null}
            {upcoming.map((s) => (
              <Link
                key={`${s.kind}:${s.id}`}
                to={reviewHref(s.kind, s.id)}
                className="today-row today-row-up"
              >
                <span className="ink-mono" style={{ fontSize: 12, color: 'var(--ink-accent)' }}>
                  {when(new Date(s.at))}
                </span>
                <span className="today-row-text">{s.title || 'Untitled'}</span>
                <span className="today-row-cat">{s.cat || ''}</span>
              </Link>
            ))}
            {!upcoming.length && !loading ? (
              <div className="ink-muted">Nothing scheduled.</div>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
};

// For rendering a card on its own (tests, previews).
export { Card as TodayQueueCard };
export type { QueueCard };
